import * as React from 'react';
import styled, { css } from 'styled-components';
import OtpInput from 'react-otp-input';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { PadlockIcon } from '../../assets';
import { Button } from '../../primitives/Button/Button';
import { type TypographyProps } from '../../primitives/Typography/Typography';
import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';

/* ─────────────────────────── OtpVerification.Root ─────────────────────── */

export interface OtpVerificationRootProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const StyledRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
`;

const OtpVerificationRoot = React.forwardRef<HTMLDivElement, OtpVerificationRootProps>(
  function OtpVerificationRoot({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledRoot ref={ref} {...semanticProps} {...rest} />;
  }
);

OtpVerificationRoot.displayName = 'OtpVerification';

/* ─────────────────────────── OtpVerification.Icon ─────────────────────── */

export interface OtpVerificationIconProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: number;
}

const TRANSIENT_ICON = ['$size'] as const;
const shouldForwardIcon = (prop: string | number): boolean =>
  !TRANSIENT_ICON.includes(prop as typeof TRANSIENT_ICON[number]);

const StyledIcon = styled.div.withConfig({ shouldForwardProp: shouldForwardIcon })<{
  $size: number;
}>`
  width: ${({ $size }) => rem($size)};
  height: ${({ $size }) => rem($size)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${rem(24)};
  flex-shrink: 0;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const OtpVerificationIcon = React.forwardRef<HTMLDivElement, OtpVerificationIconProps>(
  function OtpVerificationIcon({ size = 80, children, ...rest }, ref) {
    return (
      <StyledIcon ref={ref} $size={size} {...rest}>
        {children ?? <PadlockIcon />}
      </StyledIcon>
    );
  }
);

OtpVerificationIcon.displayName = 'OtpVerification.Icon';

/* ─────────────────────────── OtpVerification.Title ────────────────────── */

export type OtpVerificationTitleProps = TypographyProps;

const StyledTitle = styled.h4`
  font-size: ${rem(22)};
  line-height: ${rem(25)};
  font-weight: 400;
  color: var(--ui-text, ${palette.Black});
  text-align: center;
  width: 100%;
  margin: 0 0 ${rem(8)} 0;
`;

const OtpVerificationTitle = React.forwardRef<HTMLHeadingElement, OtpVerificationTitleProps>(
  function OtpVerificationTitle({ children, ...rest }, ref) {
    return (
      <StyledTitle ref={ref as React.Ref<HTMLHeadingElement>} {...rest}>
        {children}
      </StyledTitle>
    );
  }
);

OtpVerificationTitle.displayName = 'OtpVerification.Title';

/* ─────────────────────────── OtpVerification.Description ──────────────── */

export type OtpVerificationDescriptionProps = TypographyProps;

const StyledDescription = styled.p`
  font-size: ${rem(16)};
  line-height: ${rem(22)};
  font-weight: 400;
  color: var(--ui-text, ${palette.Black});
  text-align: center;
  width: 100%;
  max-width: ${rem(360)};
  margin: 0 auto ${rem(24)} auto;
`;

const OtpVerificationDescription = React.forwardRef<
  HTMLParagraphElement,
  OtpVerificationDescriptionProps
>(function OtpVerificationDescription({ children, ...rest }, ref) {
  return (
    <StyledDescription ref={ref as React.Ref<HTMLParagraphElement>} {...rest}>
      {children}
    </StyledDescription>
  );
});

OtpVerificationDescription.displayName = 'OtpVerification.Description';

/* ─────────────────────────── OtpVerification.Input ────────────────────── */

export interface OtpVerificationInputProps {
  value: string;
  onChange: (otp: string) => void;
  numInputs?: number;
  error?: boolean;
  success?: boolean;
  disabled?: boolean;
  id?: string;
}

const StyledInputWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(8)};
  margin-bottom: ${rem(16)};
`;

const getBorderColor = (error?: boolean, success?: boolean): string => {
  if (error) return `var(--ui-status-danger, ${palette.Red})`;
  if (success) return `var(--ui-status-success, ${palette.Green})`;
  return `var(--ui-border, ${palette.LightGray})`;
};

const OtpVerificationInput: React.FC<OtpVerificationInputProps> = ({
  value,
  onChange,
  numInputs = 6,
  error,
  success,
  disabled = false,
  id,
}) => {
  const borderColor = getBorderColor(error, success);

  return (
    <StyledInputWrapper id={id}>
      <OtpInput
        value={value}
        onChange={onChange}
        numInputs={numInputs}
        isInputNum
        isDisabled={disabled}
        separator={<span style={{ width: rem(8) }} />}
        inputStyle={{
          width: rem(42),
          height: rem(42),
          borderRadius: rem(8),
          border: `1px solid ${borderColor}`,
          fontSize: rem(14),
          fontWeight: 500,
          color: disabled
            ? `var(--ui-text-subtle, ${palette.Gray})`
            : `var(--ui-text, ${palette.Black})`,
          fontFamily: 'inherit',
          background: disabled
            ? `var(--ui-background-muted, ${palette.OffWhite})`
            : `var(--ui-surface, ${palette.White})`,
          outline: 'none',
          opacity: 1,
          cursor: disabled ? 'not-allowed' : undefined,
          transition: 'border-color 200ms ease',
        }}
        focusStyle={{
          border: `1px solid var(--ui-interactive, ${palette.Purple})`,
          boxShadow: `0 0 0 ${rem(3)} color-mix(in srgb, var(--ui-interactive, ${
            palette.Purple
          }) 15%, transparent)`,
        }}
      />
    </StyledInputWrapper>
  );
};

OtpVerificationInput.displayName = 'OtpVerification.Input';

/* ─────────────────────────── OtpVerification.Error ────────────────────── */

export interface OtpVerificationErrorProps extends React.HTMLAttributes<HTMLDivElement> {}

const StyledError = styled.div`
  font-size: ${rem(12)};
  font-weight: 500;
  line-height: ${rem(14)};
  color: var(--ui-status-danger, ${palette.Red});
  margin-bottom: ${rem(12)};
  text-align: center;
`;

const OtpVerificationError = React.forwardRef<HTMLDivElement, OtpVerificationErrorProps>(
  function OtpVerificationError(props, ref) {
    return <StyledError ref={ref} {...props} />;
  }
);

OtpVerificationError.displayName = 'OtpVerification.Error';

/* ─────────────────────────── OtpVerification.Actions ──────────────────── */

export interface OtpVerificationActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: 'row' | 'column';
}

const TRANSIENT_ACTIONS = ['$direction'] as const;
const shouldForwardActions = (prop: string | number): boolean =>
  !TRANSIENT_ACTIONS.includes(prop as typeof TRANSIENT_ACTIONS[number]);

const StyledActions = styled.div.withConfig({ shouldForwardProp: shouldForwardActions })<{
  $direction: 'row' | 'column';
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  width: 100%;
  margin-top: ${rem(8)};

  ${({ $direction }) =>
    $direction === 'column' &&
    css`
      flex-direction: column;
    `}
`;

const OtpVerificationActions = React.forwardRef<HTMLDivElement, OtpVerificationActionsProps>(
  function OtpVerificationActions({ direction = 'column', ...rest }, ref) {
    return <StyledActions ref={ref} $direction={direction} {...rest} />;
  }
);

OtpVerificationActions.displayName = 'OtpVerification.Actions';

/* ─────────────────────────── OtpVerification.ResendLink ───────────────── */

export interface OtpVerificationResendLinkProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
}

const OtpVerificationResendLink = React.forwardRef<
  HTMLButtonElement,
  OtpVerificationResendLinkProps
>(function OtpVerificationResendLink({ loading, children, ...rest }, ref) {
  return (
    <Button
      ref={ref}
      type='button'
      variant='link'
      {...rest}
      loading={loading}
      label={children ?? 'Resend OTP'}
    />
  );
});

OtpVerificationResendLink.displayName = 'OtpVerification.ResendLink';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const OtpVerification = Object.assign(OtpVerificationRoot, {
  Icon: OtpVerificationIcon,
  Title: OtpVerificationTitle,
  Description: OtpVerificationDescription,
  Input: OtpVerificationInput,
  Error: OtpVerificationError,
  Actions: OtpVerificationActions,
  ResendLink: OtpVerificationResendLink,
});
